import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { Users, Plus, Search, Edit2, Trash2, Mail, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  status: 'active' | 'pending' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
}

const roleLabels: Record<string, { label: string; color: string }> = {
  admin: { label: '管理者', color: 'bg-purple-100 text-purple-700' },
  user: { label: 'ユーザー', color: 'bg-blue-100 text-blue-700' },
  viewer: { label: '閲覧者', color: 'bg-gray-100 text-gray-700' },
};

const statusLabels: Record<string, { label: string; color: string }> = {
  active: { label: '有効', color: 'bg-green-100 text-green-700' },
  pending: { label: '招待中', color: 'bg-yellow-100 text-yellow-700' },
  disabled: { label: '無効', color: 'bg-red-100 text-red-700' },
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      const response = await apiFetch('/api/users');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'ユーザー一覧の取得に失敗しました');
      }

      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Users size={28} />
            ユーザー管理
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            システムを利用するユーザーを管理します
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          ユーザーを招待
        </button>
      </div>

      {/* 検索 */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Search size={18} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="名前またはメールアドレスで検索..."
            className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            style={{
              backgroundColor: 'var(--background-card)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* メッセージ表示 */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span className="text-sm">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}

      {/* ユーザー一覧 */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  ユーザー
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  ロール
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  ステータス
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  最終ログイン
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                    ユーザーが見つかりません
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                          style={{ backgroundColor: 'var(--accent-color)' }}
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {user.name}
                          </p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${roleLabels[user.role]?.color}`}
                      >
                        {roleLabels[user.role]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusLabels[user.status]?.color}`}
                      >
                        {statusLabels[user.status]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString('ja-JP')
                        : '未ログイン'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="編集"
                        >
                          <Edit2 size={16} style={{ color: 'var(--text-secondary)' }} />
                        </button>
                        <button
                          onClick={() => setDeletingUser(user)}
                          disabled={user.id === currentUser?.id}
                          className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={user.id === currentUser?.id ? '自分自身は無効化できません' : '無効化'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 招待モーダル */}
      {showInviteModal && (
        <InviteUserModal onClose={() => setShowInviteModal(false)} onSuccess={() => { fetchUsers(); setSuccess('招待メールを送信しました'); }} />
      )}

      {/* 編集モーダル */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          currentUserId={currentUser?.id}
          onClose={() => setEditingUser(null)}
          onSuccess={() => { fetchUsers(); setSuccess('ユーザーを更新しました'); setEditingUser(null); }}
        />
      )}

      {/* 削除確認モーダル */}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onSuccess={() => { fetchUsers(); setSuccess('ユーザーを無効化しました'); setDeletingUser(null); }}
        />
      )}
    </div>
  );
}

// 編集モーダルコンポーネント
function EditUserModal({ user, currentUserId, onClose, onSuccess }: { user: User; currentUserId?: string; onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [status, setStatus] = useState(user.status);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, role, status }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '更新に失敗しました');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md rounded-lg shadow-xl animate-slideIn" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>ユーザー編集</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>メールアドレス</label>
              <input type="email" value={user.email} readOnly className="w-full px-4 py-2.5 border rounded-lg bg-gray-100 cursor-not-allowed" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>名前</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>ロール</label>
              <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user' | 'viewer')} disabled={user.id === currentUserId} className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="user">ユーザー</option>
                <option value="viewer">閲覧者</option>
                <option value="admin">管理者</option>
              </select>
              {user.id === currentUserId && <p className="text-xs mt-1 text-orange-600">自分自身のロールは変更できません</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>ステータス</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'pending' | 'disabled')} className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="active">有効</option>
                <option value="pending">招待中</option>
                <option value="disabled">無効</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
              <button type="submit" disabled={loading} className="btn-primary">{loading ? '更新中...' : '更新'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// 削除確認モーダルコンポーネント
function DeleteUserModal({ user, onClose, onSuccess }: { user: User; onClose: () => void; onSuccess: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleDelete() {
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '無効化に失敗しました');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md rounded-lg shadow-xl animate-slideIn" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-red-600">ユーザー無効化</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <p style={{ color: 'var(--text-primary)' }}>
            <strong>{user.name}</strong>（{user.email}）を無効化しますか？
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            無効化されたユーザーはログインできなくなります。
          </p>

          <div className="flex justify-end gap-3 pt-6">
            <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
            <button onClick={handleDelete} disabled={loading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
              {loading ? '処理中...' : '無効化する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 招待モーダルコンポーネント
function InviteUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'user' | 'viewer'>('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch('/api/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '招待に失敗しました');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-md rounded-lg shadow-xl animate-slideIn"
        style={{ backgroundColor: 'var(--background-card)' }}
      >
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            ユーザーを招待
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                メールアドレス
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <Mail size={18} />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                ロール
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'user' | 'viewer')}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="user">ユーザー</option>
                <option value="viewer">閲覧者</option>
                <option value="admin">管理者</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? '送信中...' : '招待を送信'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

